import React, { useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles, Send, Copy, AtSign, RefreshCw } from "lucide-react";

export default function Generate() {
  const { gmailConnected, gmailEmail, usage, plan } = useAuth();
  const [jd, setJd] = useState("");
  const [tone, setTone] = useState("professional");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [detectedEmail, setDetectedEmail] = useState("");
  const [previewId, setPreviewId] = useState(null);

  const remaining = Math.max((plan?.quota || 0) - (usage?.sent || 0), 0);

  // Load draft from history if user clicked "Continue editing"
  React.useEffect(() => {
    const draft = sessionStorage.getItem("aplica_draft");
    if (draft) {
      try {
        const d = JSON.parse(draft);
        setJd(d.jd_snippet || "");
        setSubject(d.subject || "");
        setBody(d.body || "");
        setToEmail(d.to_email || "");
        setDetectedEmail(d.detected_email || "");
        setPreviewId(d.id || null);
      } catch {}
      sessionStorage.removeItem("aplica_draft");
    }
  }, []);

  const generate = async () => {
    if (jd.trim().length < 30) {
      toast.error("Paste a longer job description (30+ chars)");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/email/generate", { job_description: jd, tone });
      setSubject(data.subject);
      setBody(data.body);
      setDetectedEmail(data.detected_email || "");
      setPreviewId(data.id || null);
      if (data.detected_email) setToEmail(data.detected_email);
      toast.success("Saved to Previews");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const send = async () => {
    if (!gmailConnected) {
      toast.error("Connect Gmail in Profile first");
      return;
    }
    if (!toEmail) { toast.error("Enter recipient email"); return; }
    if (!subject || !body) { toast.error("Subject and body are required"); return; }
    setSending(true);
    try {
      await api.post("/email/send", {
        to_email: toEmail, subject, body, jd_snippet: jd.slice(0, 500), preview_id: previewId,
      });
      toast.success("Sent from your Gmail ✓");
      setJd(""); setSubject(""); setBody(""); setToEmail(""); setDetectedEmail(""); setPreviewId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="label-xs">Generate</span>
          <h1 className="mt-2 text-3xl md:text-4xl font-display tracking-tighter">Draft & send outreach.</h1>
        </div>
        <div className="text-sm text-slate-500">
          {remaining} of {plan?.quota || 0} emails left
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: JD Input */}
        <div className="tech-card p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <span className="label-xs">Job description</span>
            <select value={tone} onChange={(e) => setTone(e.target.value)}
              data-testid="tone-select"
              className="text-xs border border-slate-200 bg-white rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#0047FF]/20">
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
              <option value="confident">Confident</option>
            </select>
          </div>
          <textarea
            rows={18}
            value={jd}
            onChange={(e) => setJd(e.target.value)}
            placeholder="Paste the full job description here… (Upwork, LinkedIn, Twitter DMs — anything goes)"
            data-testid="paste-jd-textarea"
            className="flex-1 w-full border border-slate-200 bg-white rounded-md px-4 py-3 font-mono text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none resize-none"
          />
          <button onClick={generate} disabled={loading} data-testid="generate-email-button"
            className="btn-primary mt-4 justify-center">
            {loading ? (<><RefreshCw className="h-4 w-4 animate-spin" /> Drafting…</>) : (<><Sparkles className="h-4 w-4" /> Draft email</>)}
          </button>
        </div>

        {/* RIGHT: Preview + Send */}
        <div className="tech-card p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <span className="label-xs">Preview</span>
            {detectedEmail && (
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800" data-testid="detected-email-chip">
                <AtSign className="h-3 w-3" /> Detected: {detectedEmail}
              </span>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <label className="label-xs block mb-1.5">To</label>
              <input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                data-testid="to-email-input"
                placeholder="hirer@company.com"
                className="w-full border border-slate-200 bg-white rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none font-mono text-sm" />
            </div>
            <div>
              <label className="label-xs block mb-1.5">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)}
                data-testid="subject-input"
                placeholder="AI will generate a subject…"
                className="w-full border border-slate-200 bg-white rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none" />
            </div>
            <div>
              <label className="label-xs block mb-1.5">Body</label>
              <textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)}
                data-testid="body-input"
                placeholder="The AI-generated email will appear here. You can edit before sending."
                className="w-full border border-slate-200 bg-white rounded-md px-4 py-3 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none resize-y" />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-slate-500">
              {gmailConnected ? <>Sending from <span className="font-mono text-slate-900">{gmailEmail || "your Gmail"}</span></> : (
                <Link to="/profile" className="text-[#0047FF] hover:underline">Connect Gmail to send →</Link>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { navigator.clipboard.writeText(`${subject}\n\n${body}`); toast.success("Copied"); }}
                disabled={!subject && !body}
                data-testid="copy-button"
                className="px-4 py-2.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                <Copy className="h-4 w-4" /> Copy
              </button>
              <button onClick={send} disabled={sending || !gmailConnected || !toEmail || !subject || !body}
                data-testid="send-email-button"
                className="btn-primary">
                {sending ? "Sending…" : "Send via Gmail"} <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
